import React from "react";
import { useTranslation } from "react-i18next";
import AppTable from "../../components/ui/AppTable";
import ListToolbar from "../../components/ui/ListToolbar";
import AppButton from "../../components/ui/AppButton";
import { useUniAdminAuth } from "../../context/UniAdminAuthContext";
import { PERMISSIONS } from "../../utils/permissionKeys";

const UMDepartmentList = ({
  departments, loading, totalElements, campuses, faculties, campusFilter, facultyFilter,
  onCampusFilterChange, onFacultyFilterChange, searchTerm, onSearchChange, onClearFilters,
  onCreate, onEdit, onDelete, onReactivate, statusFilter, onStatusFilterChange,
}) => {
  const { t } = useTranslation("universityManager");
  const { t: tCommon } = useTranslation("common");
  const { can } = useUniAdminAuth();

  const isDeleted = statusFilter === false;
  const canFilterByCampus = can(PERMISSIONS.campus.list);
  const canFilterByFaculty = can(PERMISSIONS.faculty.list);

  const filteredFaculties = campusFilter
    ? faculties.filter((f) => String(f.campusId) === String(campusFilter))
    : faculties;

  const searchColClass = canFilterByCampus && canFilterByFaculty ? "col-md-3" : !canFilterByCampus && !canFilterByFaculty ? "col-md-7" : "col-md-5";
  const actionsColClass = canFilterByCampus && canFilterByFaculty ? "col-md-5" : !canFilterByCampus && !canFilterByFaculty ? "col-md-5" : "col-md-7";

  return (
    <div className="department-list">
      <ListToolbar
        title={t("departments.list.title")}
        subtitle={t("departments.list.subtitle", { count: totalElements })}
        filterBadges={(searchTerm || campusFilter || facultyFilter) ? (
          <>
            {searchTerm && <span className="badge bg-primary">Search: "{searchTerm}"</span>}
            {campusFilter && <span className="badge bg-info">Campus: {campuses.find((c) => c.id.toString() === campusFilter)?.nameLocal}</span>}
            {facultyFilter && <span className="badge bg-info">Faculty: {faculties.find((f) => f.id.toString() === facultyFilter)?.nameLocal}</span>}
          </>
        ) : null}
      >
        <div className="row g-3">
          <div className={searchColClass}>
            <label className="form-label fw-medium">{t("departments.list.searchLabel")}</label>
            <div className="input-group">
              <span className="input-group-text bg-light border-end-0"><i className="fas fa-search text-muted"></i></span>
              <input type="text" className="form-control border-start-0" placeholder={t("departments.list.searchPlaceholder")} value={searchTerm} onChange={onSearchChange} />
              {searchTerm && <button type="button" className="btn btn-outline-danger" onClick={() => onSearchChange({ target: { value: "" } })} title={tCommon("buttons.clear")}><i className="fas fa-times"></i></button>}
            </div>
          </div>
          {canFilterByCampus && (
            <div className="col-md-2">
              <label className="form-label fw-medium">{t("departments.list.filterByCampus")}</label>
              <select className="form-select" value={campusFilter} onChange={(e) => { onCampusFilterChange(e.target.value); onFacultyFilterChange(""); }}>
                <option value="">{t("departments.list.allCampuses")}</option>
                {campuses.map((campus) => <option key={campus.id} value={campus.id}>{campus.nameLocal || campus.nameEn}</option>)}
              </select>
            </div>
          )}
          {canFilterByFaculty && (
            <div className="col-md-2">
              <label className="form-label fw-medium">{t("departments.list.filterByFaculty")}</label>
              <select className="form-select" value={facultyFilter} onChange={(e) => onFacultyFilterChange(e.target.value)} disabled={!campusFilter && faculties.length > 20}>
                <option value="">{t("departments.list.allFaculties")}</option>
                {filteredFaculties.map((faculty) => <option key={faculty.id} value={faculty.id}>{faculty.nameLocal || faculty.nameEn}</option>)}
              </select>
            </div>
          )}
          <div className={`${actionsColClass} d-flex align-items-end justify-content-md-end gap-2 flex-wrap`}>
            {(searchTerm || campusFilter || facultyFilter) && (
              <AppButton variant="outline" className="btn-sm" onClick={onClearFilters}>
                <i className="fas fa-times me-1"></i>{tCommon("buttons.clearFilters")}
              </AppButton>
            )}
            <div className="btn-group" role="group">
              <button
                type="button"
                className={`btn btn-sm ${statusFilter === true ? "btn-primary" : "btn-outline-secondary"}`}
                onClick={() => onStatusFilterChange(true)}
              >
                <i className="fas fa-check-circle me-1"></i>
                {t("departments.list.filterActive")}
              </button>
              <button
                type="button"
                className={`btn btn-sm ${statusFilter === false ? "btn-danger" : "btn-outline-secondary"}`}
                onClick={() => onStatusFilterChange(false)}
              >
                <i className="fas fa-trash-alt me-1"></i>
                {t("departments.list.filterDeleted")}
              </button>
            </div>
            {!isDeleted && can(PERMISSIONS.department.create) && (
              <AppButton variant="primary" className="btn-sm" onClick={onCreate}>
                <i className="fas fa-plus me-1"></i>{t("crud.addDepartment")}
              </AppButton>
            )}
          </div>
        </div>
      </ListToolbar>

      <div className="card border-0 shadow-sm">
        <div className="card-body p-0">
          {loading ? (
            <div className="text-center py-5">
              <div className="spinner-border text-primary" role="status"><span className="visually-hidden">{tCommon("actions.loading")}</span></div>
              <p className="text-muted mt-2">{t("departments.list.loading")}</p>
            </div>
          ) : departments.length === 0 ? (
            <div className="text-center py-5">
              <i className="fas fa-sitemap text-muted mb-3" style={{ fontSize: "3rem" }}></i>
              <h5 className="text-muted">{t("departments.list.noFound")}</h5>
              <p className="text-muted">{(searchTerm || campusFilter || facultyFilter) ? t("departments.list.adjustFilters") : t("departments.list.noneCreated")}</p>
            </div>
          ) : (
            <AppTable responsive>
              <thead>
                <tr>
                  <th className="ps-4">{t("departments.list.columns.department")}</th>
                  <th>{t("departments.list.columns.faculty")}</th>
                  <th>{t("departments.list.columns.campus")}</th>
                  <th className="text-center">{t("departments.list.columns.students")}</th>
                  <th className="text-center pe-4">{t("crud.actions")}</th>
                </tr>
              </thead>
              <tbody>
                {departments.map((department) => (
                  <tr key={department.id} className="border-bottom">
                    <td className="ps-4">
                      <div className="fw-bold text-dark">{department.nameLocal || department.nameEn}</div>
                      <small className="text-muted">{department.nameEn}</small>
                    </td>
                    <td>
                      <div className="text-dark"><i className="fas fa-graduation-cap me-1 text-primary"></i>{department.facultyName}</div>
                    </td>
                    <td>
                      <div className="text-muted"><i className="fas fa-building me-1"></i>{department.campusName}</div>
                    </td>
                    <td className="text-center">
                      <span className="badge bg-info bg-opacity-10 text-info fw-bold">
                        <i className="fas fa-user-graduate me-1"></i>{department.studentCount?.toLocaleString() || 0}
                      </span>
                    </td>
                    <td className="text-center pe-4">
                      {isDeleted ? (
                        can(PERMISSIONS.department.update) && (
                          <AppButton
                            variant="outline"
                            className="btn-sm text-success border-success"
                            onClick={() => onReactivate && onReactivate(department)}
                            title={t("crud.reactivate")}
                          >
                            <i className="fas fa-undo me-1"></i>
                            {t("crud.reactivate")}
                          </AppButton>
                        )
                      ) : (
                        <div className="btn-group" role="group">
                          {can(PERMISSIONS.department.update) && (
                            <AppButton
                              variant="outline"
                              className="btn-sm text-primary border-primary"
                              onClick={() => onEdit && onEdit(department)}
                              title={tCommon("buttons.edit")}
                            >
                              <i className="fas fa-edit"></i>
                            </AppButton>
                          )}
                          {can(PERMISSIONS.department.delete) && (
                            <AppButton
                              variant="outline"
                              className="btn-sm text-danger border-danger"
                              onClick={() => onDelete && onDelete(department)}
                              title={tCommon("buttons.delete")}
                            >
                              <i className="fas fa-trash"></i>
                            </AppButton>
                          )}
                        </div>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </AppTable>
          )}
        </div>
      </div>
    </div>
  );
};

export default UMDepartmentList;
